import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Agriculture as AgricultureIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import { getApprovedFarmers } from '../../services/admin/adminService';

/**
 * Composant pour afficher les agriculteurs approuvés
 */
const ApprovedFarmers = () => {
  const [approvedFarmers, setApprovedFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Charger les agriculteurs approuvés
  useEffect(() => {
    fetchApprovedFarmers();
  }, []);

  const fetchApprovedFarmers = async () => {
    try {
      setLoading(true);
      const farmers = await getApprovedFarmers();
      setApprovedFarmers(farmers);
    } catch (err) {
      console.error('Erreur lors du chargement des agriculteurs approuvés:', err);
      setError('Impossible de charger les agriculteurs approuvés. ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les agriculteurs en fonction du terme de recherche
  const filteredFarmers = approvedFarmers.filter(farmer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      farmer.name?.toLowerCase().includes(searchLower) ||
      farmer.email?.toLowerCase().includes(searchLower) ||
      farmer.farmName?.toLowerCase().includes(searchLower) ||
      farmer.location?.toLowerCase().includes(searchLower) ||
      farmer.phoneNumber?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: '#014527', fontWeight: 'bold' }}>
        Agriculteurs approuvés
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* Champ de recherche */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Rechercher un agriculteur..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ 
          p: 3, 
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredFarmers.length > 0 ? (
            <List>
              {filteredFarmers.map((farmer, index) => (
                <React.Fragment key={farmer.id}>
                  <ListItem>
                    <ListItemIcon>
                      <AgricultureIcon sx={{ color: '#014527' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={farmer.name} 
                      secondary={
                        <>
                          <Typography component="span" variant="body2">
                            Email: {farmer.email}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2">
                            Téléphone: {farmer.phoneNumber || 'Non spécifié'}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2">
                            Ferme: {farmer.farmName || 'Non spécifié'} ({farmer.location || 'Lieu non spécifié'})
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2">
                            Cultures: {farmer.crops || 'Non spécifié'}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2" color="text.secondary">
                            Approuvé le: {formatDate(farmer.approvedAt)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < filteredFarmers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
              {searchTerm ? 'Aucun agriculteur ne correspond à votre recherche.' : 'Aucun agriculteur approuvé pour le moment.'}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ApprovedFarmers;